import { FrameworkControlAnnualWheelActivitySelectorDialogProps } from "./FrameworkControlAnnualWheelActivitySelectorDialog.tsx";
import { useState } from "react";
import { post } from "@common/api/apiShared.ts";
import { useDotLegalMutation, useDotLegalQuery } from "@common/hooks/dotLegalReactQuery.ts";
import { SelectableAnnualWheelActivityTableRow } from "@common/components/selectableAnnualWheelActivityTable/SelectableAnnualWheelActivityTable.types.ts";
import { addMonths } from "date-fns";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../../localization/useTranslation.ts";
import { RecurringIntervalType } from "../../../../annualWheel/AnnualWheel.types.ts";

export interface StandardAnnualWheelActivityTemplateSelection {
    templateId: string;
    deadline: Date;
    interval: number | null;
    intervalType: RecurringIntervalType | null;
    groupEntityIds: Array<string>;
}

interface FrameworkControlAnnualWheelActivitySaveModel {
    templates: Array<StandardAnnualWheelActivityTemplateSelection>;
}

export function useFrameworkControlAnnualWheelActivitySelectorDialog(props: FrameworkControlAnnualWheelActivitySelectorDialogProps) {
    const snackbar = useDotLegalSnackbar();

    const { translateString } = useTranslation();

    const [saveModel, setSaveModel] = useState<FrameworkControlAnnualWheelActivitySaveModel>({
        templates: [],
    });

    const url = `/frameworkControls/${props.controlTemplateId}/overview/selectable-control-template-annual-wheel-activities`;
    const { isLoading, data } = useDotLegalQuery<Array<SelectableAnnualWheelActivityTableRow>>(url);

    const saveMutation = useDotLegalMutation(createAnnualWheelActivitiesApi);
    const onCreateAnnualWheelActivities = () => {
        saveMutation.mutateAsync(undefined, {
            onSuccess: () => {
                snackbar.show(translateString("standardTemplateAnnualWheelActivityAddedToControl"));
                props.onAnnualWheelActivitiesSaved();
            },
        });
    };

    const modelUpdater = {
        onAnnualWheelActivityAdd: (id: string) => {
            const template = data?.find((x) => x.id === id);
            const newSelection: StandardAnnualWheelActivityTemplateSelection = {
                templateId: id,
                deadline: addMonths(new Date(), 1),
                interval: template?.recurringInterval ?? null,
                intervalType: template?.recurringIntervalType ?? null,
                groupEntityIds: [],
            };
            setSaveModel({ ...saveModel, templates: [...saveModel.templates, newSelection] });
        },
        onAnnualWheelActivityRemove: (id: string) => {
            setSaveModel({ ...saveModel, templates: saveModel.templates.filter((x) => x.templateId !== id) });
        },
        onTemplateDeadlineChange: (id: string, deadline: Date) => {
            setSaveModel({
                ...saveModel,
                templates: saveModel.templates.map((x) => (x.templateId === id ? { ...x, deadline } : x)),
            });
        },
        onTemplateRecurrenceChange: (id: string, recurrence: { interval: number | null; intervalType: RecurringIntervalType | null }) => {
            setSaveModel({
                ...saveModel,
                templates: saveModel.templates.map((x) =>
                    x.templateId === id ? { ...x, interval: recurrence.interval, intervalType: recurrence.intervalType } : x
                ),
            });
        },
        onTemplateGroupEntitiesChange: (id: string, groupEntityIds: Array<string>) => {
            const current = saveModel.templates.find((x) => x.templateId === id);
            const previous = current?.groupEntityIds ?? [];
            let next = groupEntityIds;

            if (previous.length !== 0 && !previous.includes("null") && next.includes("null")) {
                next = [];
            } else if (next.length > 1) {
                next = next.filter((x) => x !== "null");
            }

            setSaveModel({
                ...saveModel,
                templates: saveModel.templates.map((x) => (x.templateId === id ? { ...x, groupEntityIds: next } : x)),
            });
        },
    };

    return {
        isLoading,
        data,
        saveModel,
        modelUpdater,
        onCreateAnnualWheelActivities,
        isSaving: saveMutation.isLoading,
    };

    function createAnnualWheelActivitiesApi() {
        return post(`/frameworkControls/${props.controlId}/standard-annual-wheel-activities`, saveModel);
    }
}
